import { ERROR_MESSAGES_RU, type ApiErrorBody, type ErrorCode, type FieldError } from "@cafe/shared";
import i18n from "@/shared/i18n";
import { requestApproval } from "./approval";
import { describeApiError } from "./error-text";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | "NETWORK_ERROR",
    message: string,
    readonly details?: Record<string, unknown> & { fields?: FieldError[] },
    readonly requestId?: string,
  ) {
    super(message);
  }
  get fields(): FieldError[] {
    return this.details?.fields ?? [];
  }
}

type Session = {
  getToken: () => string | null;
  getBranchId: () => string | null;
  refresh: () => Promise<boolean>;
  onUnauthorized: () => void;
};

let session: Session = { getToken: () => null, getBranchId: () => null, refresh: async () => false, onUnauthorized: () => {} };
/** Связывает клиент с хранилищем сессии (без циклического импорта). */
export const configureApi = (s: Session) => (session = s);

type Options = {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  auth?: boolean;
  /** false — без X-Branch-Id (например, /me до выбора филиала). */
  branch?: boolean;
  /** Вернуть { items, meta } (списки с пагинацией). */
  withMeta?: boolean;
  headers?: Record<string, string>;
};

type Problem = { type: string; title: string; status: number; code: string; errors?: { field: string; message: string }[]; details?: Record<string, unknown>; instance?: string };
const PROBLEM_CODES: Record<string, ErrorCode> = {
  validation_failed: "VALIDATION_ERROR", token_expired: "TOKEN_EXPIRED", token_invalid: "UNAUTHENTICATED", forbidden: "PERMISSION_DENIED",
  terminal_revoked: "TERMINAL_REVOKED", version_conflict: "VERSION_CONFLICT", cursor_expired: "CURSOR_EXPIRED", batch_too_large: "BATCH_TOO_LARGE", rate_limited: "RATE_LIMITED",
} as Record<string, ErrorCode>;
/** Код контракта кассы (snake_case) → код ошибки ERP. */
export const problemCode = (c: string): ErrorCode => PROBLEM_CODES[c] ?? (c.toUpperCase() as ErrorCode);
/** problem+json (RFC 9457) → общий формат ошибки ERP. */
const fromProblem = (p: Problem): ApiErrorBody => ({
  success: false,
  error: {
    code: PROBLEM_CODES[p.code] ?? (p.code.toUpperCase() as ErrorCode),
    message: p.title,
    details: { ...p.details, ...(p.errors && { fields: p.errors.map((e) => ({ path: e.field, rule: "invalid", message: e.message })) }) },
    requestId: p.instance,
  },
});

let refreshing: Promise<boolean> | null = null;
/** Один refresh на все параллельные 401 (очередь запросов). */
const refreshOnce = () => (refreshing ??= session.refresh().finally(() => (refreshing = null)));

export async function api<T>(method: string, path: string, opts: Options = {}, retry = true): Promise<T> {
  const url = new URL(`/api/v1${path}`, window.location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));

  const headers: Record<string, string> = { Accept: "application/json", "Accept-Language": i18n.language };
  const isForm = opts.body instanceof FormData;
  if (opts.body !== undefined && !isForm) headers["Content-Type"] = "application/json";
  const token = opts.auth === false ? null : session.getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const branchId = session.getBranchId();
  if (branchId && opts.auth !== false && opts.branch !== false) headers["X-Branch-Id"] = branchId;
  Object.assign(headers, opts.headers);

  let res: Response;
  try {
    res = await fetch(url, { method, headers, body: opts.body === undefined ? undefined : isForm ? (opts.body as FormData) : JSON.stringify(opts.body), credentials: "same-origin", signal: opts.signal });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiError(0, "NETWORK_ERROR", i18n.t("errors.NETWORK_ERROR"));
  }

  if (res.status === 204) return undefined as T;
  const raw = (await res.json().catch(() => null)) as ({ success: true; data: T; meta?: unknown } | ApiErrorBody | Problem | null);
  // Пути контракта кассы (/terminals, /venue, /sync…) отвечают без обёртки, ошибки — problem+json
  if (res.ok && raw && !("success" in raw)) return raw as T;
  const json = raw && "code" in raw && !("success" in raw) ? fromProblem(raw as Problem) : (raw as { success: true; data: T; meta?: unknown } | ApiErrorBody | null);

  if (res.ok && json?.success) return (opts.withMeta ? { items: json.data, meta: json.meta } : json.data) as T;

  const err = json && !json.success ? json.error : undefined;
  const code = err?.code ?? (res.status >= 500 ? "INTERNAL_ERROR" : "VALIDATION_ERROR");
  if (res.status === 401 && token && retry && (code === "TOKEN_EXPIRED" || code === "UNAUTHENTICATED")) {
    if (await refreshOnce()) return api<T>(method, path, opts, false);
    session.onUnauthorized();
  }
  // Выше лимита роли: PIN менеджера → повтор с токеном (ТЗ 04 §2.1)
  if (code === "APPROVAL_REQUIRED" && !opts.headers?.["X-Approval-Token"]) {
    const approval = await requestApproval(err?.details);
    if (approval) return api<T>(method, path, { ...opts, headers: { ...opts.headers, "X-Approval-Token": approval } }, retry);
  }
  throw new ApiError(res.status, code, err?.message ?? ERROR_MESSAGES_RU[code] ?? i18n.t("errors.requestFailed"), err?.details, err?.requestId);
}

export const http = {
  get: <T>(path: string, query?: Options["query"], signal?: AbortSignal) => api<T>("GET", path, { query, signal }),
  post: <T>(path: string, body?: unknown, opts?: Omit<Options, "body">) => api<T>("POST", path, { ...opts, body }),
  put: <T>(path: string, body?: unknown) => api<T>("PUT", path, { body }),
  patch: <T>(path: string, body?: unknown, headers?: Record<string, string>) => api<T>("PATCH", path, { body, headers }),
  delete: <T>(path: string) => api<T>("DELETE", path),
};

/** Понятное сообщение для пользователя: причина ошибки с подробностями (см. `describeApiError`). */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return describeApiError(e);
  return i18n.t("errors.unknown");
}
